import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  formatTimeValue,
  roundedNowTimeParts,
  TimePicker,
} from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import { combineDateAndTime, dateAndTimeToHHMM } from "@/pages/TaskResults";
import { formatDateKey } from "@/lib/time";
import { DatePickerInput } from "@/components/foundations/inputs/DatePickerInput/DatePickerInput";
import { shiftTimeRangeDate } from "@/lib/dateSplit";

export type StartStopTarget = "start" | "end";

type Colors = {
  bar: string;
  soft: string;
  ink: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTarget: StartStopTarget;
  dateKey: string;
  endDateKey?: string;
  splitDate?: boolean;
  scheduledTime?: string | null;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  colors: Colors;
  onConfirm: (input: { target: StartStopTarget; when: Date | null; dateKey: string }) => void;
};

export function resolveTimePickerDateKey(dateKey: string, now = new Date()) {
  return dateKey || formatDateKey(now);
}

export function StartStopTimeDialog({
  open,
  onOpenChange,
  initialTarget,
  dateKey,
  endDateKey,
  splitDate = false,
  scheduledTime,
  startedAt,
  completedAt,
  colors,
  onConfirm,
}: Props) {
  const defaultStartTime = useMemo(
    () => dateAndTimeToHHMM(toDate(startedAt)) || scheduledTime || nowTimeString(),
    [scheduledTime, startedAt],
  );
  const defaultEndTime = useMemo(
    () => dateAndTimeToHHMM(toDate(completedAt)) || dateAndTimeToHHMM(toDate(startedAt)) || scheduledTime || nowTimeString(),
    [completedAt, scheduledTime, startedAt],
  );
  const [target, setTarget] = useState<StartStopTarget>(initialTarget);
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [startDate, setStartDate] = useState(dateKey);
  const [endDate, setEndDate] = useState(endDateKey || dateKey);

  useEffect(() => {
    if (!open) return;
    setTarget(initialTarget);
    setStartTime(defaultStartTime);
    setEndTime(defaultEndTime);
    setStartDate(dateKey);
    setEndDate(endDateKey || dateKey);
  }, [dateKey, defaultEndTime, defaultStartTime, endDateKey, initialTarget, open]);

  const selectedTime = target === "start" ? startTime : endTime;
  const setSelectedTime = target === "start" ? setStartTime : setEndTime;
  const selectedDate = target === "start" ? startDate : endDate;
  const setSelectedDate = target === "start" ? setStartDate : setEndDate;
  const localSplitDate = Boolean(startDate && endDate && startDate !== endDate);

  function setNow() {
    const { h, m } = roundedNowTimeParts();
    setSelectedTime(formatTimeValue(h, m));
  }

  function confirm() {
    const confirmedDate = resolveTimePickerDateKey(selectedDate || dateKey);
    onConfirm({ target, when: selectedTime ? combineDateAndTime(confirmedDate, selectedTime) : null, dateKey: confirmedDate });
    onOpenChange(false);
  }

  function changeSelectedDay(offset: -1 | 1) {
    const next = shiftTimeRangeDate({
      target,
      startDate,
      endDate,
      startTime,
      endTime,
      offset,
    });
    setStartDate(next.startDate);
    setEndDate(next.endDate);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="ui-dialog-content z-[110] w-[min(22rem,calc(100%-2rem))] rounded-2xl p-5 shadow-2xl"
      >
        <DialogTitle className="sr-only">Record task time</DialogTitle>
        <StartStopTimeInput
          target={target}
          onTargetChange={setTarget}
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
          onClear={() => setSelectedTime("")}
          onNow={setNow}
          dateKey={selectedDate}
          onDateChange={setSelectedDate}
          showDate={localSplitDate}
          onDayOffsetChange={changeSelectedDay}
          colors={colors}
        />
        <div className="grid grid-cols-2 gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirm}
            style={{ background: colors.bar, color: colors.soft }}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StartStopTimeInput({
  target,
  onTargetChange,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  onClear,
  onNow,
  dateKey,
  onDateChange,
  showDate = false,
  onDayOffsetChange,
  colors,
}: {
  target: StartStopTarget;
  onTargetChange: (target: StartStopTarget) => void;
  startTime: string;
  endTime: string;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onClear: () => void;
  onNow: () => void;
  dateKey?: string;
  onDateChange?: (value: string) => void;
  showDate?: boolean;
  onDayOffsetChange?: (offset: -1 | 1) => void;
  colors: Colors;
}) {
  const selectedTime = target === "start" ? startTime : endTime;
  const setSelectedTime = target === "start" ? onStartTimeChange : onEndTimeChange;

  return (
    <div className="ui-start-stop space-y-3">
      <div className="ui-tab-bar grid grid-cols-2 rounded-xl bg-muted p-1">
        {(["start", "end"] as const).map(value => {
          const active = target === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onTargetChange(value)}
              className="ui-tab-button h-10 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: active ? colors.bar : "transparent",
                color: active ? colors.soft : colors.ink,
              }}
            >
              {value === "start" ? "Start" : "End"}
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {showDate && onDateChange && (
          <DatePickerInput
            value={dateKey || null}
            onChange={onDateChange}
            dateFormat="long"
            showCalendarIcon
          />
        )}
        <TimePicker
          value={selectedTime}
          onChange={setSelectedTime}
          accentColor={colors.bar}
          onDayOffsetChange={onDayOffsetChange}
        />
      </div>

      <div className="flex justify-between border-b border-border/60 pt-3 text-sm">
        <button
          type="button"
          onClick={onClear}
          className="font-medium transition-opacity hover:opacity-60"
          style={{ color: colors.ink }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onNow}
          className="font-medium transition-opacity hover:opacity-60 inline-block p-4 rounded-t-base"
          style={{ color: colors.ink }}
        >
          Now
        </button>
      </div>

    </div>
  );
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function nowTimeString(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}
